import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, History, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { readJson } from '../lib/fetchHelpers';
import { SEO } from '../components/SEO';

import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Locale map for Intl date formatting (was referenced but never defined -> ReferenceError swallowed, ISO date shown)
const LOCALE_MAP = { no: 'nb-NO', en: 'en-GB', so: 'so-SO', ar: 'ar', fr: 'fr-FR', es: 'es-ES' };

const formatDateNo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return new Intl.DateTimeFormat(LOCALE_MAP[(i18n.language || 'no').split('-')[0]] || 'nb-NO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
};

const BookingCard = ({ booking, past, t }) => (
  <div
    data-testid={`booking-${booking.id}`}
    className={`bg-white rounded-xl border ${past ? 'border-slate-200 opacity-80' : 'border-green-500/40'} p-5 shadow-sm`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
      <div>
        <p className="text-xs text-green-600 font-bold uppercase tracking-widest">{formatDateNo(booking.date)}</p>
        <h3 className="text-xl font-bold text-slate-900 mt-1">
          {t('tennis.court')} {booking.court_number} · {booking.time_slot}
        </h3>
      </div>
      {past ? (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 ${booking.status === 'cancelled' ? 'text-red-600 bg-red-500/10' : 'text-slate-500 bg-slate-100'}`}>
          <History className="w-3 h-3" />
          {t(booking.status === 'cancelled' ? 'tennis.toastCancelled' : 'myBookings.completed')}
        </span>
      ) : booking.status === 'cancelled' ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-500/10 rounded-full px-3 py-1">
          {t('tennis.toastCancelled')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-500/10 rounded-full px-3 py-1">
          <CheckCircle2 className="w-3 h-3" />
          {t('myBookings.confirmed')}
        </span>
      )}
    </div>
    <div className="grid sm:grid-cols-3 gap-3 text-sm text-slate-600">
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-green-600" />
        <span>{booking.duration_hours} {t(booking.duration_hours === 1 ? 'tennis.hour' : 'tennis.hours')}</span>
      </div>
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-green-600" />
        <span>{t('tennis.location')}</span>
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-green-600" />
        <span className="capitalize">{t(booking.status === 'cancelled' ? 'tennis.toastCancelled' : 'myBookings.confirmed')}</span>
      </div>
    </div>
  </div>
);

export const TennisMyBookings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('tennis_token');
    if (!token) {
      navigate('/tennis/login');
      return;
    }
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tennis/bookings/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await readJson(res);
        if (!res.ok) throw new Error(data.detail || t('myBookings.loadError'));
        setUpcoming((data.upcoming || []).filter((b) => b.status !== 'cancelled'));
        setPast(data.past || []);
      } catch (err) {
        toast.error(err.message || t('myBookings.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  return (
    <div data-testid="my-bookings-page" className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
      <SEO title={t('myBookings.title')} description={t('myBookings.subtitle')} />
      <div className="max-w-4xl mx-auto">
        <Link
          to="/tennis/booking"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('myBookings.backToBooking')}
        </Link>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-green-600 font-medium uppercase tracking-widest text-sm">Tennis</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white tracking-tight">{t('myBookings.title')}</h1>
          </div>
          <Button
            data-testid="book-new-link"
            onClick={() => navigate('/tennis/booking')}
            className="bg-green-500 text-black font-bold hover:bg-green-500/90"
          >
            {t('myBookings.bookNew')}
          </Button>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-12" data-testid="mine-bookinger-loading">
            {t('myBookings.loading')}
          </p>
        ) : (
          <>
            <section className="mb-12">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                {t('myBookings.upcoming')}
                <span className="text-sm font-normal text-slate-500" data-testid="upcoming-count">
                  ({upcoming.length})
                </span>
              </h2>
              {upcoming.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center">
                  <p className="text-slate-500 mb-4">{t('myBookings.noUpcoming')}</p>
                  <Button
                    onClick={() => navigate('/tennis/booking')}
                    className="bg-green-500 text-black font-bold hover:bg-green-500/90"
                  >
                    {t('myBookings.bookNow')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3" data-testid="upcoming-list">
                  {upcoming.map((b) => (
                    <BookingCard key={b.id} booking={b} past={false} t={t} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                {t('myBookings.past')}
                <span className="text-sm font-normal text-slate-500" data-testid="past-count">
                  ({past.length})
                </span>
              </h2>
              {past.length === 0 ? (
                <p className="text-slate-500 text-sm">{t('myBookings.noPast')}</p>
              ) : (
                <div className="space-y-3" data-testid="past-list">
                  {past.map((b) => (
                    <BookingCard key={b.id} booking={b} past t={t} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default TennisMyBookings;
